import { matchPath, NavLink, Outlet, useLocation } from "react-router-dom";
import { OrgSwitcher } from "@/features/auth/OrgSwitcher";
import { UserMenu } from "@/components/UserMenu";

const NAV = [
  { to: "/", label: "Dashboard" },
  { to: "/crm", label: "CRM" },
  { to: "/crm/pipeline", label: "Pipeline" },
  { to: "/quotes", label: "Quotes" },
  { to: "/projects", label: "Projects" },
  { to: "/invoices", label: "Invoices" },
  { to: "/operations", label: "Operations" },
  { to: "/catalogue", label: "Catalogue" },
  { to: "/settings", label: "Settings" },
] as const;

const OPS_NAV = [
  { to: "/operations", label: "Home", end: true },
  { to: "/operations/suppliers", label: "Suppliers" },
  { to: "/operations/pos", label: "Purchase Orders" },
  { to: "/operations/dockets", label: "Delivery Dockets" },
] as const;

/**
 * App-wide chrome.
 *
 * Two header modes:
 *   • Browse mode (default) — full nav row with Dashboard/CRM/.../Settings
 *     links, the workspace switcher, and the user menu. Used on every
 *     top-level page.
 *   • Workspace mode — thin 38px strip with logo, breadcrumb back-link,
 *     workspace switcher, and user menu. Triggered by routes that benefit
 *     from extra vertical space, currently only the quote editor
 *     (`/quotes/:quoteId`). Page-specific actions/tabs live inside the
 *     route component itself.
 *
 * The Operations sub-nav strip below the main header is unaffected by
 * workspace mode — it only appears when `inOperations` is true.
 */
export function AppShell() {
  const location = useLocation();
  const inOperations = location.pathname.startsWith("/operations");

  // Workspace mode: only inside a specific quote (`/quotes/:quoteId`).
  // The list view (`/quotes`) keeps the full nav.
  const inQuoteEditor =
    matchPath("/quotes/:quoteId", location.pathname) !== null;

  return (
    <div className="min-h-screen flex flex-col">
      {inQuoteEditor ? <WorkspaceHeader /> : <BrowseHeader />}

      {inOperations && (
        <div
          className="flex items-center gap-1 px-4 py-2 border-b border-border"
          style={{ background: "var(--neo-bg2)" }}
        >
          {OPS_NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={"end" in n ? n.end : undefined}
              className={({ isActive }) =>
                `px-2.5 py-1 rounded text-xs font-semibold transition ${
                  isActive ? "bg-teal/15 text-teal" : "text-fg-muted hover:text-fg"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
      )}

      <main className="flex-1 bg-bg0">
        <Outlet />
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------
// Header variants
// ---------------------------------------------------------------------

/** Standard 52px-tall header with full nav. */
function BrowseHeader() {
  return (
    <header className="bg-bg1 border-b border-border px-4 py-3 flex items-center gap-6">
      <Logo />
      <nav className="flex items-center gap-1 text-sm flex-1">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            className={({ isActive }) =>
              `px-3 py-1.5 rounded transition ${
                isActive
                  ? "bg-navy text-white"
                  : "text-fg-muted hover:text-fg hover:bg-bg2"
              }`
            }
          >
            {n.label}
          </NavLink>
        ))}
      </nav>
      <OrgSwitcher />
      <UserMenu />
    </header>
  );
}

/**
 * 38px-tall header for the quote editor — strips the global nav back to
 * a logo + back-crumb so the quote workspace gets nearly all the
 * vertical chrome. Click the logo (or the back-crumb) to return to the
 * Quotes list. Org switcher + user menu remain accessible on the right.
 */
function WorkspaceHeader() {
  return (
    <header
      className="border-b border-border px-4 flex items-center gap-4"
      style={{
        background: "var(--neo-navy)",
        height: 38,
        flexShrink: 0,
      }}
    >
      <Logo small />
      <NavLink
        to="/quotes"
        className="text-xs text-fg-muted hover:text-teal transition flex items-center gap-1"
        title="Back to all quotes"
      >
        <span className="opacity-70">←</span> Quotes
      </NavLink>
      <span className="flex-1" />
      <OrgSwitcher />
      <UserMenu compact />
    </header>
  );
}

function Logo({ small }: { small?: boolean }) {
  return (
    <NavLink to="/" className="flex items-baseline gap-2 hover:opacity-80 transition" title="Home">
      <span
        className={`font-bold tracking-tight text-fg ${small ? "text-base" : "text-lg"}`}
      >
        Neo.
      </span>
      <span
        className={`font-mono uppercase tracking-widest text-teal ${small ? "text-[9px]" : "text-[10px]"}`}
      >
        next
      </span>
    </NavLink>
  );
}
